import type { CurrencyLedger, ResolvedItem } from '@fd/settlement-engine';

// A month settled by bank transfer settles itself (the owner, 01.10.2026: "jak samo wykryje dany
// przelew na tę kwotę, to zatwierdza się wszystko samo wraz z notatką i dostaje się powiadomienie").
// Pure: the screen hands in each finished month's settlement and the bank entries; out comes the one
// settlement to record, or nothing.

/**
 * The mark an automatic settlement carries in `ruleSnapshot` (a free field nothing else uses), so the
 * bell can say it settled by itself.
 */
export const AUTO_SETTLED = { autoSettled: 'bankTransfer' } as const;

export function isAutoSettled(settlement: { readonly ruleSnapshot: unknown }): boolean {
  const snapshot = settlement.ruleSnapshot as { autoSettled?: unknown } | null;
  return snapshot !== null && typeof snapshot === 'object' && snapshot.autoSettled === AUTO_SETTLED.autoSettled;
}

/** How long after the month a payment for it may come. */
const PAYMENT_WINDOW_DAYS = 45;
const DAY_MS = 86_400_000;
const addDays = (iso: string, days: number) => new Date(Date.parse(`${iso}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);

export type AutoSettleEntry = {
  readonly id: string;
  readonly accountId: string;
  readonly bookingDate: string;
  readonly amountMinor: bigint;
  readonly currency: string;
  readonly kind: string;
  readonly status: string;
  readonly source: string;
  readonly paymentType: string | null;
  readonly deleted: boolean;
};

export type AutoSettleMonth = {
  readonly start: string;
  readonly end: string;
  readonly ledgers: readonly CurrencyLedger[];
  readonly items: readonly ResolvedItem[];
};

type AutoSettlement = {
  readonly start: string;
  readonly end: string;
  readonly currency: string;
  readonly transfers: readonly { readonly fromPersonId: string; readonly toPersonId: string; readonly amountMinor: bigint; readonly paymentTransactionId: string }[];
  readonly items: readonly { readonly transactionId: string; readonly splitId: string | null }[];
};

/**
 * The first finished month whose every suggested transfer has been paid by bank transfer: exactly one
 * booked bank transfer of exactly the amount, into an account of the person paid only (or, when that
 * side is not in the app, out of an account of the payer only), from the month's first day to 45 days
 * after it, not behind a settlement already. A month with a payment still pending at the bank waits:
 * its amount may change when the bank books or cancels it.
 */
export function findAutoSettlement(input: {
  readonly today: string;
  readonly months: readonly AutoSettleMonth[];
  readonly entries: readonly AutoSettleEntry[];
  readonly ownersOf: (accountId: string) => readonly string[];
  /** Bank entries a settlement already names as its payment. */
  readonly usedPayments: ReadonlySet<string>;
}): AutoSettlement | null {
  const byId = new Map(input.entries.map((e) => [e.id, e]));
  const soleOwner = (accountId: string, personId: string) => {
    const owners = input.ownersOf(accountId);
    return owners.length === 1 && owners[0] === personId;
  };
  for (const month of input.months) {
    if (month.end >= input.today) continue;
    for (const ledger of month.ledgers) {
      if (ledger.suggestedTransfers.length === 0) continue;
      const items = month.items.filter((item) => item.currency === ledger.currency);
      if (items.some((item) => byId.get(item.transactionId)?.status === 'PENDING')) continue;
      const claimed = new Set<string>();
      const transfers: AutoSettlement['transfers'][number][] = [];
      for (const transfer of ledger.suggestedTransfers) {
        const fits = input.entries.filter(
          (e) =>
            !e.deleted &&
            e.source === 'BANK' &&
            e.status === 'BOOKED' &&
            e.currency === ledger.currency &&
            (e.paymentType === 'TRANSFER' || e.kind === 'TRANSFER') &&
            e.kind !== 'SETTLEMENT_PAYMENT' &&
            e.kind !== 'BALANCE_ADJUSTMENT' &&
            !input.usedPayments.has(e.id) &&
            !claimed.has(e.id) &&
            e.bookingDate >= month.start &&
            e.bookingDate <= addDays(month.end, PAYMENT_WINDOW_DAYS) &&
            e.bookingDate <= input.today,
        );
        // Both sides of one payment may be in the app (out of the payer's account, into the payee's):
        // the side that reached the person paid stands for it.
        const into = fits.filter((e) => e.amountMinor === transfer.amountMinor && soleOwner(e.accountId, transfer.toPersonId));
        const out = fits.filter((e) => e.amountMinor === -transfer.amountMinor && soleOwner(e.accountId, transfer.fromPersonId));
        const payment = into.length === 1 ? into[0] : into.length === 0 && out.length === 1 ? out[0] : undefined;
        if (!payment) break;
        claimed.add(payment.id);
        transfers.push({ fromPersonId: transfer.fromPersonId, toPersonId: transfer.toPersonId, amountMinor: transfer.amountMinor, paymentTransactionId: payment.id });
      }
      if (transfers.length !== ledger.suggestedTransfers.length) continue;
      return { start: month.start, end: month.end, currency: ledger.currency, transfers, items: items.map((item) => ({ transactionId: item.transactionId, splitId: item.splitId })) };
    }
  }
  return null;
}
